import { Card, Col, Form, InputNumber, Row, Space, Switch } from 'antd';
import { observer } from 'mobx-react-lite';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import configurationService from 'common/services/configuration-service/configuration.service';
import { IRentalPortalConfigurationUpdate } from 'common/services/configuration-service/configuration.service.types';
import { notification } from 'common/utils/notification';
import configurationStore from 'common/stores/configuration/configuration.store';
import { useEffect, useRef } from 'react';

const AUTOSAVE_DELAY_MS = 1500;

interface IRentalPortalFormValues {
  isPropertyFeeEnabled: boolean;
  propertyRegistrationFee: number;
  propertyRenewalFee: number;
  isReservationFeeEnabled: boolean;
  reservationStandardFee: number;
  reservationExpeditedFee: number;
  reservationExpressFee: number;
  isAgencyChangeFeeEnabled: boolean;
  agencyChangeFee: number;
  minimumReservationNights: number;
}

interface IFeeFormItemProps {
  name: keyof IRentalPortalFormValues;
  label: string;
  disabled: boolean;
}

const FeeFormItem = ({ name, label, disabled }: IFeeFormItemProps) => (
  <Space className="full-width">
    <Form.Item name={name} label={label} rules={[{ required: true, message: `${label} is required` }]}>
      <InputNumber min={0} step={0.01} precision={2} disabled={disabled} style={{ width: '100%' }} />
    </Form.Item>
  </Space>
);

export const RentalPortalConfiguration = observer(() => {
  const [form] = Form.useForm<IRentalPortalFormValues>();
  const initData = configurationStore.configuration?.rentalPortal;
  const isPropertyFeeEnabled = Form.useWatch('isPropertyFeeEnabled', form);
  const isReservationFeeEnabled = Form.useWatch('isReservationFeeEnabled', form);
  const isAgencyChangeFeeEnabled = Form.useWatch('isAgencyChangeFeeEnabled', form);
  const autosaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isFormInitializedRef = useRef(false);
  const saveSequenceRef = useRef(0);

  useEffect(() => {
    if (initData && !isFormInitializedRef.current) {
      isFormInitializedRef.current = true;
      form.setFieldsValue(initData);
    }
  }, [form, initData]);

  useEffect(() => () => clearTimeout(autosaveTimeoutRef.current), []);

  const handleFormValuesChange = () => {
    clearTimeout(autosaveTimeoutRef.current);

    autosaveTimeoutRef.current = setTimeout(() => {
      form.submit();
    }, AUTOSAVE_DELAY_MS);
  };

  const onFinish = async (values: IRentalPortalFormValues) => {
    const saveSequence = ++saveSequenceRef.current;
    const model: IRentalPortalConfigurationUpdate = {
      rentalPortal: { ...values },
    };

    try {
      const result = await configurationService.updateSystemConfiguration(model);

      if (saveSequence !== saveSequenceRef.current) {
        return;
      }

      configurationStore.setConfiguration(result);
      notification.success({
        message: 'Configurations successfully updated',
        duration: 5,
      });
    } catch {
      notification.error({
        message: 'Failed to update the Rental Portal configuration',
        duration: 5,
      });
    }
  };

  if (!initData) {
    return null;
  }

  return (
    <FormWrapper form={form} onValuesChange={handleFormValuesChange} onFinish={onFinish} layout="vertical">
      <Card className="eiq-card eiq-card-border eiq-configuration">
        <Row className="eiq-card-header">
          <Col span={24}>
            <h1 className="h1">Rental Portal</h1>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <h3>Property registration fees</h3>
            <Space className="full-width">
              <Form.Item name="isPropertyFeeEnabled" label="Charge property registration fees" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
            <FeeFormItem name="propertyRegistrationFee" label="Registration fee ($)" disabled={isPropertyFeeEnabled === false} />
            <FeeFormItem name="propertyRenewalFee" label="Annual renewal fee ($)" disabled={isPropertyFeeEnabled === false} />
            <h3>Agency change fee</h3>
            <Space className="full-width">
              <Form.Item name="isAgencyChangeFeeEnabled" label="Charge agency change fee" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
            <FeeFormItem name="agencyChangeFee" label="Agency change fee ($)" disabled={isAgencyChangeFeeEnabled === false} />
          </Col>
          <Col xs={24} sm={12}>
            <h3>Reservation application fees</h3>
            <Space className="full-width">
              <Form.Item name="isReservationFeeEnabled" label="Charge reservation application fees" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
            <FeeFormItem name="reservationStandardFee" label="Standard fee ($)" disabled={isReservationFeeEnabled === false} />
            <FeeFormItem name="reservationExpeditedFee" label="Expedited fee ($)" disabled={isReservationFeeEnabled === false} />
            <FeeFormItem name="reservationExpressFee" label="Express fee ($)" disabled={isReservationFeeEnabled === false} />
            <h3>Reservations</h3>
            <Space className="full-width">
              <Form.Item
                name="minimumReservationNights"
                label="Minimum reservation length (nights)"
                rules={[{ required: true, message: 'Minimum reservation length is required' }]}>
                <InputNumber min={1} max={365} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Space>
          </Col>
        </Row>
      </Card>
    </FormWrapper>
  );
});
